import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { FaAngleRight, FaChevronDown, FaChevronRight } from 'react-icons/fa6';
import { ListOption } from './ListSelect';
import { GroupHeaderRow } from './ListSelectAutocomplete.styles';

type GroupHeaderProps = {
  group: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
};

const GroupHeader = ({ group, count, expanded, onToggle }: GroupHeaderProps) => {
  const segments = group.split('/');
  return (
    <GroupHeaderRow role="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={onToggle}>
      {expanded ? (
        <FaChevronDown size={11} className="opacity-70" />
      ) : (
        <FaChevronRight size={11} className="opacity-70" />
      )}
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          {index > 0 && <FaAngleRight className="opacity-50" size={12} />}
          <span>{segment}</span>
        </React.Fragment>
      ))}
      <span className="ml-auto font-normal tabular-nums opacity-70">{count}</span>
    </GroupHeaderRow>
  );
};

type ListSelectProps<T> = {
  options: ListOption<T>[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  render?: (value: T) => React.ReactNode;
};

function ListSelectAutocomplete<T>(props: ListSelectProps<T>) {
  const { options, value, onChange, label = 'Select an option', required, disabled, render } = props;
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [inputValue, setInputValue] = useState('');
  const isFiltering = inputValue.trim().length > 0;

  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups((prev) => {
      const groups = new Set(prev);
      if (groups.has(group)) {
        groups.delete(group);
      } else {
        groups.add(group);
      }
      return groups;
    });
  }, []);

  const handleAutocompleteChange = useCallback(
    (_: SyntheticEvent, newValue: { label: string; id: any } | null) => {
      onChange(newValue?.id || null);
    },
    [onChange],
  );

  const validOptions = useMemo(
    () =>
      (options || []).map((option: any) => ({
        label: `${option.value} - (${option.const.split('-').pop()})`,
        id: option.const,
        group: option.group as string | undefined,
      })),
    [JSON.stringify(options)],
  );

  const hasGroups = useMemo(() => validOptions.some((o) => o.group), [validOptions]);

  const invalidOption = useMemo(() => {
    if (!validOptions.find((o) => o.id === value) && value) {
      return { label: value, id: value, group: undefined };
    }
    return null;
  }, [JSON.stringify(options), value]);

  const autocompleteOptions = useMemo(() => {
    const allOptions = validOptions.concat(invalidOption ? [invalidOption] : []);
    if (!hasGroups) return allOptions;

    return [...allOptions].sort((a, b) => {
      const groupCompare = (a.group ?? '').localeCompare(b.group ?? '');
      return groupCompare !== 0 ? groupCompare : a.label.localeCompare(b.label);
    });
  }, [validOptions, invalidOption, hasGroups]);

  const selectedValue = useMemo(() => {
    return autocompleteOptions.find((o) => o.id === value) ?? null;
  }, [JSON.stringify(options), value]);

  const renderOption = useCallback(
    (option: { label: string; id: string }) => {
      return invalidOption && option.id === invalidOption.id ? (
        <span className="text-red-500">Invalid option</span>
      ) : render ? (
        render(option.id as T)
      ) : option.label.includes(' - ') ? (
        option.label.split(' - ').slice(0, -1).join(' - ')
      ) : (
        option.label
      );
    },
    [render],
  );

  return (
    <Autocomplete
      value={selectedValue}
      onChange={handleAutocompleteChange}
      options={autocompleteOptions}
      groupBy={hasGroups ? (option) => option.group || 'Ungrouped' : undefined}
      renderGroup={(params) => {
        const expanded = isFiltering || expandedGroups.has(params.group);
        return (
          <li key={params.key}>
            <GroupHeader
              group={params.group}
              count={React.Children.count(params.children)}
              expanded={expanded}
              onToggle={() => toggleGroup(params.group)}
            />
            {expanded && <ul style={{ padding: 0 }}>{params.children}</ul>}
          </li>
        );
      }}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={() => ' '}
      blurOnSelect
      clearOnBlur
      isOptionEqualToValue={(option, value) => value != null && option.id === value.id}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {renderOption(option)}
        </li>
      )}
      renderInput={(params) => (
        <div className="relative">
          <TextField
            {...params}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            label={label}
            placeholder={selectedValue ? '' : 'Select or search an option'}
            required={required && !selectedValue} // hack to allow empty label in the input
            error={!!invalidOption}
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none min-w-[85%]">
            {!isInputFocused && selectedValue ? renderOption(selectedValue) : null}
          </div>
        </div>
      )}
      fullWidth
      disabled={disabled}
      className="flex-grow min-w-[200px]"
      filterOptions={(options, state) => {
        const query = state.inputValue.toLowerCase();
        return options.filter(
          (option) => option.label.toLowerCase().includes(query) || (option.group ?? '').toLowerCase().includes(query),
        );
      }}
    />
  );
}

export default ListSelectAutocomplete;
